import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryClient } from "./query-client";
import type { PublicAppStatus } from "./app-status-policy";

const statusKey = ["app-status"] as const;
const rejectedKey = ["app-status", "maintenance-rejection"] as const;

// The root shell owns polling; consumers share the cache without extra timers.
export function useAppStatus(poll = false) {
  const client = useQueryClient();
  const rejection = useQuery({ queryKey: rejectedKey, queryFn: skipToken, initialData: false });
  const status = useQuery({
    queryKey: statusKey,
    queryFn: async ({ signal }): Promise<PublicAppStatus> => {
      const response = await fetch("/api/status", {
        signal: AbortSignal.any([signal, AbortSignal.timeout(10_000)]),
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!response.ok) throw new Error("status-unavailable");
      const body = (await response.json()) as PublicAppStatus;
      signal.throwIfAborted();
      client.setQueryData(rejectedKey, false);
      return body;
    },
    staleTime: 60_000,
    // Directory cards also consume status. Their mounting must not restart a
    // failed initial request and repeatedly replace home with its splash.
    retryOnMount: false,
    refetchInterval: poll ? 60_000 : false,
    refetchOnWindowFocus: poll ? "always" : false,
  });
  return { ...status, maintenance: Boolean(rejection.data || status.data?.maintenance.enabled) };
}

// A policy rejection takes effect immediately, even if status was unavailable.
// Only a subsequent successful status response clears this fallback.
export function reportMaintenance() {
  queryClient.setQueryData(rejectedKey, true);
  void queryClient.invalidateQueries({ queryKey: statusKey, exact: true });
}
